import React, { useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { getAnnotationLabelOption, labelOptionIn, type AnnotationLabelOption } from "./annotationLabels";
import {
  assignAnnotationSpanLanes,
  buildAnnotationTextSegments,
  type AnnotationEntityLike,
  type SpannedAnnotationEntity,
} from "./annotationSpans";
import AppButton from "../../ui/buttons/AppButton";
import IconButton from "../../ui/buttons/IconButton";
import { classNames } from "../../ui/styles/classNames";
import { clearSelection, selectedRangeIn } from "../../shared/selection/selectedRange";
import AnchoredTooltip from "../../ui/overlays/AnchoredTooltip";
import { useAnchoredTooltip } from "../../ui/overlays/useAnchoredTooltip";
import AnnotationEditControls from "./AnnotationEditControls";
import AnnotationSpanList, { AnnotationTooltipText } from "./AnnotationSpanList";
import { useTextEvidence, type EvidenceLayer } from "./textEvidence";
import {
  ANNOTATION_LABEL_FONT_PX,
  ANNOTATION_LABEL_RISE_REM,
  ANNOTATION_LANE_GAP_REM,
  ANNOTATION_LANE_OFFSET_REM,
  ANNOTATION_LANE_PADDING_REM,
  getAnnotationLabelAnchorPercent,
  getAnnotationLabelColorStyle,
  getAnnotationLabelDisplayName,
  getAnnotationLabelMaxWidth,
  getTextOffsetWithinContainer,
  getVisualCenterOffset,
  splitAnnotationTextSegments,
} from "./annotationLabelLayout";

export interface EditableAnnotationEntity extends AnnotationEntityLike {
  id: string;
  /** What the annotator wrote about the span: in its tooltip and the list under the text. */
  note?: string;
  /** Why the server refused the span: shown with it under the text, its label tag outlined. */
  problems?: readonly string[];
}

interface AnnotationTextEditorProps {
  title?: string;
  text: string;
  entities: Array<SpannedAnnotationEntity<EditableAnnotationEntity>>;
  /** Read-only: the evidence drawn on the text and followed from it. */
  evidence?: EvidenceLayer;
  /** Off: the text sits on the page itself, as a document's reading view shows it, not in a box. */
  framed?: boolean;
  labelOptions?: AnnotationLabelOption[];
  /** The readable name of each label key; a key without one shows as itself. */
  labelNames?: ReadonlyMap<string, string>;
  /**
   * Off: a read-only view. Bars and label tags are then decoration, hidden from screen
   * readers, which read each span from the list under the text; only the label tag of a
   * span with a note takes focus, to show the note.
   */
  editable?: boolean;
  selectedId?: string | null;
  emptyText?: string;
  selectLabel: string;
  addLabel: string;
  deleteLabel: string;
  /** Names the list of the spans under the text, such as "Annotations in petition 1". */
  annotationListLabel: string;
  onAddAnnotation?: (annotation: {
    type: string;
    value: string;
    start: number;
    end: number;
  }) => void;
  onUpdateAnnotationType?: (entityId: string, type: string) => void;
  onDeleteAnnotation?: (entityId: string) => void;
  onSelectAnnotation?: (entityId: string | null) => void;
}

interface PendingSelection {
  start: number;
  end: number;
  value: string;
  type: string;
}


export default function AnnotationTextEditor({
  title,
  text,
  entities,
  evidence,
  framed = true,
  labelOptions = [],
  labelNames,
  editable = false,
  selectedId = null,
  emptyText,
  selectLabel,
  addLabel,
  deleteLabel,
  annotationListLabel,
  onAddAnnotation,
  onUpdateAnnotationType,
  onDeleteAnnotation,
  onSelectAnnotation,
}: AnnotationTextEditorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [pendingSelection, setPendingSelection] = useState<PendingSelection | null>(null);
  const tooltip = useAnchoredTooltip<ReadonlyArray<SpannedAnnotationEntity<EditableAnnotationEntity>>>();
  const noteIdPrefix = useId();
  const [annotationFont, setAnnotationFont] = useState("");
  const [annotationLabelFont, setAnnotationLabelFont] = useState("");
  // The span editor uses clicks and selections itself, so only a read-only view follows evidence.
  const textEvidence = useTextEvidence(editable ? undefined : evidence);
  const segments = useMemo(() => buildAnnotationTextSegments(text, entities, textEvidence.boundaries), [entities, textEvidence.boundaries, text]);
  const renderSegments = useMemo(() => splitAnnotationTextSegments(segments), [segments]);
  const annotationLanes = useMemo(() => assignAnnotationSpanLanes(entities), [entities]);
  const laneByAnnotation = useMemo(
    () =>
      new Map(annotationLanes.map(({ annotation, lane }) => [annotation, lane] as const)),
    [annotationLanes]
  );
  const labelAnchorByAnnotation = useMemo(
    () =>
      new Map(
        entities.map(
          (annotation) =>
            [
              annotation,
              getVisualCenterOffset(text.slice(annotation.start, annotation.end), annotationFont),
            ] as const
        )
      ),
    [annotationFont, entities, text]
  );
  const nameOf = (type: string) => labelNames?.get(type) ?? type;
  const describe = (annotation: SpannedAnnotationEntity<EditableAnnotationEntity>) => `${nameOf(annotation.type)}: ${annotation.value}`;
  // A span with a note or problems has something to say: it is listed, and its tag takes focus.
  const noteIdOf = (annotation: SpannedAnnotationEntity<EditableAnnotationEntity>) =>
    annotation.note || (annotation.problems?.length ?? 0) > 0 ? `${noteIdPrefix}-note-${annotation.id}` : undefined;

  const maxLane = annotationLanes.reduce((maximum, { lane }) => Math.max(maximum, lane), 0);
  const selectedEntity = entities.find((entity) => entity.id === selectedId) || null;
  const selectableLabelOptions = useMemo(() => {
    const names = new Set<string>();
    const options: AnnotationLabelOption[] = [];

    // add all labels from github-fetched label list
    labelOptions.forEach((labelOption) => {
      if (names.has(labelOption.name)) {
        return;
      }
      names.add(labelOption.name);
      options.push(labelOption);
    });

    // also add the label of the currently selected entity and pending selection, if they are not already in the list, so that they can be displayed in the dropdown even if they are deprecated
    [selectedEntity?.type, pendingSelection?.type].forEach((labelName) => {
      if (!labelName || names.has(labelName)) {
        return;
      }
      names.add(labelName);
      options.unshift(getAnnotationLabelOption(labelName));
    });

    return options;
  }, [labelOptions, pendingSelection?.type, selectedEntity?.type]);

  useLayoutEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const style = window.getComputedStyle(containerRef.current);
    const nextFont = `${style.fontStyle} ${style.fontVariant} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
    const nextLabelFont = `${style.fontStyle} ${style.fontVariant} 600 ${ANNOTATION_LABEL_FONT_PX}px ${style.fontFamily}`;
    setAnnotationFont(nextFont);
    setAnnotationLabelFont(nextLabelFont);
  }, []);

  const defaultLabelName = labelOptions[0]?.name;
  if (defaultLabelName && pendingSelection && !pendingSelection.type) {
    setPendingSelection({ ...pendingSelection, type: defaultLabelName });
  }

  const selectAnnotation = (
    event: React.MouseEvent,
    annotation: SpannedAnnotationEntity<EditableAnnotationEntity>
  ) => {
    event.stopPropagation();
    if (editable) {
      onSelectAnnotation?.(annotation.id);
    }
  };

  const selectMostSpecificAnnotation = (
    event: React.MouseEvent,
    annotations: Array<SpannedAnnotationEntity<EditableAnnotationEntity>>
  ) => {
    if (!editable || annotations.length === 0) {
      return;
    }

    event.stopPropagation();
    const annotation = [...annotations].sort(
      (left, right) => (laneByAnnotation.get(right) || 0) - (laneByAnnotation.get(left) || 0)
    )[0];
    if (annotation) {
      onSelectAnnotation?.(annotation.id);
    }
  };

  const handleMouseUp = () => {
    if (!editable || !containerRef.current) {
      return;
    }

    const range = selectedRangeIn(containerRef.current);
    if (range === null) {
      return;
    }

    const start = getTextOffsetWithinContainer(
      containerRef.current,
      range.startContainer,
      range.startOffset
    );
    const end = getTextOffsetWithinContainer(
      containerRef.current,
      range.endContainer,
      range.endOffset
    );

    if (start === null || end === null || start === end) {
      return;
    }

    const normalizedStart = Math.max(Math.min(start, end), 0);
    const normalizedEnd = Math.min(Math.max(start, end), text.length);
    const value = text.slice(normalizedStart, normalizedEnd);
    if (!value.trim()) {
      return;
    }

    setPendingSelection({
      start: normalizedStart,
      end: normalizedEnd,
      value,
      type: labelOptions[0]?.name || "",
    });
    onSelectAnnotation?.(null);
  };

  const handleAddAnnotation = () => {
    if (!pendingSelection || !pendingSelection.type) {
      return;
    }

    onAddAnnotation?.(pendingSelection);
    setPendingSelection(null);
    clearSelection();
  };

  const renderAnnotationLabel = (
    annotation: SpannedAnnotationEntity<EditableAnnotationEntity>,
    lane: number,
    anchorPercent: number
  ) => {
    const labelOption = labelOptionIn(labelOptions, annotation.type);
    const displayName = getAnnotationLabelDisplayName(
      nameOf(annotation.type),
      annotation.value,
      annotationFont,
      annotationLabelFont
    );
    const style = {
      left: `${anchorPercent}%`,
      top: `${ANNOTATION_LANE_OFFSET_REM + lane * ANNOTATION_LANE_GAP_REM - ANNOTATION_LABEL_RISE_REM}rem`,
      maxWidth: getAnnotationLabelMaxWidth(annotation.value, annotationFont),
      ...getAnnotationLabelColorStyle(labelOption),
    };
    const hasSay = noteIdOf(annotation) !== undefined;
    const className = classNames(
      // Not selectable, so a copied passage holds the text without its label tags.
      "absolute z-10 box-border inline-flex min-w-0 -translate-x-1/2 select-none items-center justify-center truncate rounded-sm border px-0.5 py-0.5 text-[11px] font-semibold leading-none shadow-sm ring-1",
      selectedId === annotation.id && "outline outline-2 outline-offset-1 outline-blue-500",
      selectedId !== annotation.id && (annotation.problems?.length ?? 0) > 0 && "outline outline-2 outline-offset-1 outline-[var(--app-danger)]",
      !editable && (hasSay ? "cursor-help underline decoration-dotted" : "cursor-default")
    );
    if (!editable && !hasSay) {
      return (
        <span key={`${annotation.id}-label`} aria-hidden="true" style={style} className={className} {...tooltip.hoverProps([annotation])}>
          {displayName}
        </span>
      );
    }
    return (
      <button
        key={`${annotation.id}-label`}
        type="button"
        data-annotation-control={editable ? "true" : undefined}
        aria-label={describe(annotation)}
        aria-describedby={noteIdOf(annotation)}
        // Read-only, focus shows the note; the click stays here, off the text's own handler.
        onClick={(event) => { if (editable) { selectAnnotation(event, annotation); } else { event.stopPropagation(); } }}
        style={style}
        className={className}
        {...tooltip.hoverProps([annotation])}
        {...tooltip.focusProps([annotation])}
      >
        {displayName}
      </button>
    );
  };

  return (
    <section>
      {title ? (
        <h3 className="app-title mb-3 text-base font-semibold">{title}</h3>
      ) : null}

      <div
        ref={containerRef}
        onClick={(event) => { onSelectAnnotation?.(null); textEvidence.select(event.target); }}
        {...textEvidence.followProps}
        onMouseUp={handleMouseUp}
        className={classNames("app-text whitespace-pre-wrap break-words text-sm leading-8", framed ? "app-card-muted min-h-20 p-4" : "py-1")}
      >
        {segments.length > 0 ? (
          renderSegments.map((segment) => {
            const segmentIsAnnotated = segment.annotations.length > 0;
            const segmentCanShowBars = segmentIsAnnotated && !/[\r\n]/.test(segment.text);
            const labelAnchors = segment.annotations
              .map((annotation) => ({
                annotation,
                anchorPercent: getAnnotationLabelAnchorPercent(
                  annotation,
                  segment,
                  labelAnchorByAnnotation.get(annotation) || 0,
                  annotationFont
                ),
              }))
              .filter(
                (
                  anchor
                ): anchor is {
                  annotation: SpannedAnnotationEntity<EditableAnnotationEntity>;
                  anchorPercent: number;
                } => anchor.anchorPercent !== null
              );
            const selectedAnnotation = segment.annotations.find(
              (annotation) => annotation.id === selectedId
            );
            const selectedLabelOption = selectedAnnotation
              ? labelOptionIn(labelOptions, selectedAnnotation.type)
              : undefined;

            if (!segmentCanShowBars) {
              return (
                <span key={`${segment.start}-${segment.end}`} data-annotation-source="true" {...(segmentIsAnnotated ? tooltip.hoverProps(segment.annotations) : {})} {...textEvidence.segmentProps(segment.start)}>
                  {segment.text}
                </span>
              );
            }
            const { className: followedClass, ...followed } = textEvidence.segmentProps(segment.start);

            return (
              <span
                key={`${segment.start}-${segment.end}`}
                className="relative inline-block align-baseline"
                style={{
                  paddingBottom: `${ANNOTATION_LANE_PADDING_REM + maxLane * ANNOTATION_LANE_GAP_REM}rem`,
                }}
              >
                <span
                  data-annotation-source="true"
                  {...followed}
                  onClick={(event) => { selectMostSpecificAnnotation(event, segment.annotations); }}
                  {...tooltip.hoverProps(segment.annotations)}
                  className={classNames(
                    "relative z-0 px-0.5 py-0.5",
                    followedClass,
                    editable && "cursor-pointer",
                    selectedAnnotation &&
                      "rounded-sm ring-1 ring-inset outline outline-2 outline-offset-1 outline-blue-500",
                  )}
                  style={
                    selectedLabelOption
                      ? getAnnotationLabelColorStyle(selectedLabelOption)
                      : undefined
                  }
                >
                  {segment.text}
                </span>
                {segment.annotations.map((annotation) => {
                  const lane = laneByAnnotation.get(annotation) || 0;
                  const labelOption = labelOptionIn(labelOptions, annotation.type);
                  const startsRange = annotation.start === segment.start;
                  const endsRange = annotation.end === segment.end;
                  const selected = annotation.id === selectedId;

                  /**
                   * Vertically stack the annotation bars based on their assigned lane, with a fixed offset for the first lane and a gap between subsequent lanes. The top position is calculated in rem units to ensure consistent spacing
                   */
                  const style = {
                    top: `${ANNOTATION_LANE_OFFSET_REM + lane * ANNOTATION_LANE_GAP_REM}rem`,
                    backgroundColor: labelOption.barColor,
                  };
                  const className = classNames(
                    "absolute -left-px z-0 w-[calc(100%+2px)] -translate-y-1/2 appearance-none border-0 p-0",
                    selected ? "h-1.5 rounded-sm" : "h-1 rounded-[2px]",
                    startsRange && "rounded-l-sm",
                    endsRange && "rounded-r-sm",
                    selected && "shadow-sm ring-1 ring-blue-500",
                    editable ? "cursor-pointer" : "cursor-default"
                  );
                  if (!editable) {
                    return <span key={`${annotation.id}-bar`} aria-hidden="true" style={style} className={className} />;
                  }
                  return (
                    <button
                      key={`${annotation.id}-bar`}
                      type="button"
                      data-annotation-control="true"
                      aria-label={describe(annotation)}
                      onClick={(event) => { selectAnnotation(event, annotation); }}
                      style={style}
                      className={className}
                    />
                  );
                })}
                {labelAnchors.map(({ annotation, anchorPercent }) =>
                  renderAnnotationLabel(
                    annotation,
                    laneByAnnotation.get(annotation) || 0,
                    anchorPercent
                  )
                )}
              </span>
            );
          })
        ) : (
          <span className="app-text-muted text-sm">{emptyText}</span>
        )}
      </div>
      <AnnotationSpanList entities={entities} nameOf={nameOf} optionOf={(type) => labelOptionIn(labelOptions, type)} label={annotationListLabel} noteIdOf={noteIdOf} />
      {tooltip.shown === null ? null : (
        <AnchoredTooltip anchor={tooltip.shown.anchor}>
          <AnnotationTooltipText spans={tooltip.shown.content} nameOf={nameOf} />
        </AnchoredTooltip>
      )}

      {editable && selectedEntity ? (
        <AnnotationEditControls
          value={selectedEntity.value}
          type={selectedEntity.type}
          options={selectableLabelOptions}
          nameOf={nameOf}
          selectLabel={selectLabel}
          onChangeType={(type) => onUpdateAnnotationType?.(selectedEntity.id, type)}
        >
          <IconButton
            type="button"
            onClick={() => onDeleteAnnotation?.(selectedEntity.id)}
            className="app-action-link-danger h-8 w-8 shrink-0"
            title={deleteLabel}
            label={deleteLabel}
          >
            <TrashIcon className="h-4 w-4" />
          </IconButton>
        </AnnotationEditControls>
      ) : editable && pendingSelection ? (
        <AnnotationEditControls
          value={pendingSelection.value}
          type={pendingSelection.type}
          options={selectableLabelOptions}
          nameOf={nameOf}
          selectLabel={selectLabel}
          disabled={selectableLabelOptions.length === 0}
          onChangeType={(type) => { setPendingSelection((previous) => (previous ? { ...previous, type } : previous)); }}
        >
          <AppButton type="button" onClick={handleAddAnnotation} disabled={!pendingSelection.type} variant="primary" size="xs">
            <PlusIcon className="h-4 w-4" />
            {addLabel}
          </AppButton>
        </AnnotationEditControls>
      ) : null}
    </section>
  );
}
